import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { safeRead, safeWrite } from '../../../infra';
import { Places } from '../../route-consts';
import { hasAnyRole, PANEL_THEME_LABELS, PANEL_THEMES, safeId } from '../../../domain';
import { CompanySelectorPresenter, MISSING_COMPANY } from '../../../components/company-selector/company-selector.presenter';
import { RestrictedApi } from './restricted.api';
import { accountName } from './restricted.types';
import { CollaboratorsKeys } from './collaborators/collaborators.key';
import { CompaniesKeys } from './companies/companies.key';
import { CompanyUsersKeys } from './company-users/company-users.key';
import { MonitoringKeys } from './monitoring/monitoring.key';
import { MyDataKeys } from './my-data/my-data.key';
import { SleepReportKeys } from './sleep-report/sleep-report.key';
import { UserBaseKeys } from './user-base/user-base.key';
import { IcpKeys } from './icp/icp.key';
import { DashboardKeys } from './dashboard/dashboard.key';
import { RestrictedKeys } from './restricted.key';
import { NavGroupScope, NavItemScope, RestrictedScope, ThemeOptionScope } from './restricted.scope';
const LOG = Logger.get('Mormaii.RestrictedPresenter');
// @Inject
const api = RestrictedApi.INSTANCE;
/**
 * O icone de cada secao, usado no menu E no cabecalho.
 *
 * O painel antigo repete o icone nos dois lugares, e a mesma tabela servir aos
 * dois e o que impede um deles de ficar para tras.
 *
 * Os nomes sao os do Material Design Icons sem o prefixo `mdi`, e sao os MESMOS
 * que o painel antigo escolhe em `utills/dataValue.ts`. A apresentacao nao importa
 * a biblioteca - ela nomeia o icone, e quem o desenha e a view. Antes destes nomes
 * a tabela dizia `layout-dashboard` e `chart`, que eram nomes do lucide: o porte
 * desenhava outro conjunto de icones, e era uma das diferencas visiveis na tela
 * logada.
 */
const SECTION_ICONS = {
    dashboard: 'graphql',
    'user-base': 'chart-timeline-variant',
    companies: 'domain',
    'company-users': 'account-multiple-check',
    collaborators: 'account-group',
    monitoring: 'hand-heart-outline',
    'sleep-report': 'sleep',
    'my-data': 'account-edit',
    icp: 'bullseye-arrow'
};
/**
 * O endereco de cada secao, para o item do menu ser um `<a>` de verdade.
 *
 * Escrito aqui, e nao montado pela view: o caminho e o mesmo que a arvore de
 * places declara em `Routes.ts`, e quem conhece places e a apresentacao.
 */
const SECTION_HREFS = {
    dashboard: '#/hub/dashboard',
    'user-base': '#/hub/user-base',
    companies: '#/hub/companies',
    'company-users': '#/hub/company-users',
    collaborators: '#/hub/collaborators',
    monitoring: '#/hub/monitoring',
    'sleep-report': '#/hub/sleep-report',
    'my-data': '#/hub/my-data',
    icp: '#/hub/icp'
};
/**
 * A classe de keys de cada secao - como se VIAJA para ela.
 *
 * Existe para a viagem ter um lugar so. Os destinos estavam escritos um a um dentro
 * do menu, e a troca de empresa passou a precisar dos mesmos: sem a tabela, a segunda
 * lista de destinos nasceria copiada da primeira, e as duas divergiriam no dia em que
 * uma secao mudasse de place.
 *
 * Nao inclui `companies`: ela e do administrador geral, que nao escolhe empresa.
 */
const SECTION_KEYS = {
    dashboard: DashboardKeys,
    'user-base': UserBaseKeys,
    companies: CompaniesKeys,
    'company-users': CompanyUsersKeys,
    collaborators: CollaboratorsKeys,
    monitoring: MonitoringKeys,
    'sleep-report': SleepReportKeys,
    'my-data': MyDataKeys,
    icp: IcpKeys
};
/**
 * Como o painel antigo nomeia o papel de quem esta logado, no cabecalho.
 *
 * Nao e o `primaryRoleLabel` do dominio: aquele diz "Administrador" e serve ao
 * shell; este e o texto do cabecalho do painel antigo, que diz "Admin". Dois
 * lugares, dois textos - igualar os dois seria mudar um deles.
 */
function headerRoleLabel(roles) {
    if (roles.includes('ROLE_SUPER_ADMIN'))
        return 'Super Admin';
    if (roles.includes('ROLE_ADMIN'))
        return 'Admin';
    if (roles.includes('ROLE_COMPANY_ADMIN'))
        return 'Empresa';
    if (roles.includes('ROLE_PROFESSIONAL'))
        return 'Profissional';
    return 'Admin';
}
/**
 * Os dois conjuntos de papeis que o painel antigo usa para decidir o que mostrar,
 * com os nomes que ele lhes da em `utills/dataValue.ts`.
 *
 * `HUB_ROLES` responde outra pergunta - quem ENTRA no hub - e nao serve aqui.
 * Usa-la para liberar secao dava ao profissional o Painel, que o antigo nunca lhe
 * deu: a tela montava, o `POST /dashboard/analytics` respondia 403 e o leitor via
 * "Sessão expirada" logo depois de entrar.
 */
const CAN_SEE_COLLABORATORS = ['ROLE_COMPANY_ADMIN', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'];
const CAN_SEE_COMPANIES = ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN'];
/**
 * O HUB: a porta, e o que fica dentro dela.
 *
 * Toda tela que exige sessao esta abaixo deste place, e e por isso que a sessao e
 * verificada aqui, uma vez, e nao em cada tela. Uma tela de dentro nao chega a
 * rodar para depois descobrir que nao devia - retornar `false` interrompe a
 * caminhada do framework antes disso.
 *
 * Era este o papel do `ProtectRouter` no painel antigo, repetido em cada rota.
 */
/**
 * A chave onde a ultima empresa escolhida e lembrada.
 *
 * Leva o id da CONTA no fim - `mormaii.company:274` -, porque duas pessoas usam o
 * mesmo navegador e abrir na empresa de quem saiu seria pior do que nao lembrar nada.
 */
const COMPANY_MEMORY = 'mormaii.company';
/**
 * A ultima empresa escolhida no seletor de uma TELA - o do administrador geral, para
 * quem a empresa e filtro de cada modulo, e nao contexto do cabecalho. Uma chave
 * separada da de cima: as duas respondem perguntas diferentes, e nenhuma pessoa usa
 * as duas. Ver `rememberFilterCompany`.
 */
const FILTER_COMPANY_MEMORY = 'mormaii.filterCompany';
export class RestrictedPresenter extends CubePresenter {
    constructor(app) {
        super(app, new RestrictedScope());
        this.parentSlot = NOOP_VOID;
        this.contentSlot = (scope) => {
            this.scope.content = scope ?? undefined;
        };
        /** Se o nome de quem esta logado ja foi pedido. Uma vez por sessao, e nao por tela. */
        this.identityAsked = false;
        /** O seletor do cabecalho - existe so para quem administra mais de uma. */
        this.scopeSelector = new CompanySelectorPresenter(this.update, async (companyId) => {
            await this.onChooseCompany(companyId);
        }, () => this.app.alert('error', MISSING_COMPANY.title, MISSING_COMPANY.text));
        /**
         * As empresas que quem esta logado ADMINISTRA.
         *
         * Vazia para o administrador geral - ele nao administra nenhuma, ele ve todas.
         * Uma para o caso comum. Mais de uma desde que a mesma pessoa passou a poder
         * administrar varias, e e dai que sai a decisao de oferecer ou nao o seletor.
         */
        this.companies = [];
        /**
         * As empresas em que a pessoa ESTA - as que administra mais as que atende.
         *
         * Separada de `companies` de proposito. Aquela responde "quais eu administro", e e
         * o que sete telas leem para decidir se pedem uma empresa escolhida; esta responde
         * "onde eu estou", e quem a le e o CABECALHO. Misturar as duas faria o profissional
         * passar a "poder escolher empresa" aos olhos daquelas telas - e o Relatorio de
         * Sono, por exemplo, esvazia a lista de atletas quando ha escolha pendente. Ele
         * ficaria sem candidato algum por causa de um rotulo.
         */
        this.presence = [];
        /**
         * As empresas que a pessoa ATENDE como profissional - so essas.
         *
         * Terceira lista, e ela precisa existir separada das outras duas. `presence` mistura
         * atendidas com administradas de proposito, porque o cabecalho responde "onde eu
         * estou"; mas os Monitoramentos Compartilhados perguntam outra coisa - "em qual
         * empresa eu ATENDO" -, e para quem acumula os dois papeis as respostas divergem.
         *
         * Sem esta separacao, quem administra e nao atende nenhuma mandava ao
         * `/professional/patients_with_last_measurements` uma empresa ADMINISTRADA, e o
         * servidor respondia **400 "Você não está lotado nesta empresa."**
         * (`ProfessionalService.restrictToOwnCompany`). Na base de QA e o caso da conta 268,
         * que administra seis empresas e nao atende nenhuma.
         */
        this.served = [];
        /** Se ja se SABE quais empresas a pessoa administra - o par de `servedKnown`. */
        this.companiesKnown = false;
        /**
         * Se ja se SABE quais empresas a pessoa atende.
         *
         * Separado de `served.length`, porque "nenhuma" e "ainda nao perguntei" levam a
         * decisoes opostas no menu, e a pergunta pode ate falhar. Enquanto nao se sabe, o
         * item continua no lugar: esconde-lo por uma falha de rede tiraria do profissional
         * a unica tela dele.
         */
        this.servedKnown = false;
        // O menu do nome: os mesmos destinos do menu lateral, a um clique do cabecalho.
        this.scope.onOpenMyData = this.action(async () => {
            await MyDataKeys.of(this.app).flip();
        });
        this.scope.onSignOut = this.action(async () => {
            await this.app.confirmSignOut();
        });
    }
    release() {
        super.release();
        LOG.info('Finalized');
    }
    publishParameters(intent) {
        const keys = new RestrictedKeys(this.app, intent);
        /*
         * So quem ESCOLHE publica: com uma empresa so, o parametro seria ruido num
         * endereco que ja e cifrado.
         *
         * `presence` e nao `companies` porque o profissional tambem escolhe desde
         * 17/09/2026 - e sem publicar, o link que ele manda a um colega abriria na
         * empresa lembrada de QUEM RECEBE, e nao na que ele estava vendo.
         */
        keys.companyInScope = this.presence.length > 1 ? this.inScope : undefined;
    }
    async applyParameters(intent, initialization, last) {
        // A porta, uma vez, para tudo o que esta dentro do HUB.
        if (!this.app.authenticated) {
            await this.app.demandSignIn(intent);
            return false;
        }
        const keys = new RestrictedKeys(this.app, intent);
        keys.owner = this;
        if (initialization) {
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        /*
         * As empresas sao ESPERADAS, e o nome nao. Elas vem da SESSAO, carregadas no
         * login (ver `loadCompanies`): no caso normal este `await` nao vai a rede.
         *
         * `canChooseCompany` e lido pelas telas de dentro na montagem delas, e o
         * framework so desce depois que este metodo resolve - entao a lista precisa
         * estar em maos AQUI. O nome de quem esta logado e enfeite de cabecalho e
         * continua chegando quando chegar; a diferenca e que um seletor que aparece
         * meio segundo depois muda o que a tela consultou.
         *
         * Antes do `last` de proposito: a landing pergunta `allowsSection`, e o que cada
         * secao responde depende de quais empresas a pessoa administra e atende.
         * Decidir sem a lista mandaria o leitor a uma tela que a guarda recusa no passo
         * seguinte - ou, pior, a "Meus dados", que aceita todo mundo.
         */
        await this.loadCompanies();
        /*
         * A empresa de abertura, em tres degraus: o ENDERECO, a ULTIMA escolhida, a
         * mais RECENTE.
         *
         * Nao existe mais abrir sem empresa para quem tem varias - decidido em
         * 18/09/2026. Antes, quem atendia duas entrava vendo as duas misturadas, e isso
         * nao e um modo: e a ausencia de uma decisao que alguem tem de tomar, tomada
         * pelo painel em silencio.
         *
         * O endereco vem primeiro porque um link e uma escolha explicita de quem o
         * mandou. Depois a lembrada, que e a escolha explicita de quem esta entrando.
         * Por fim a mais recente - a que a pessoa acabou de ser vinculada, e portanto
         * aquela em que provavelmente esta trabalhando; o servidor ja entrega a lista
         * nessa ordem.
         *
         * O administrador GERAL segue sem empresa, e ali "nenhuma" quer dizer TODAS -
         * e o papel dele, e a regra do cliente de 17/09/2026 diz que os colaboradores
         * sem empresa sao visiveis so para ele.
         */
        this.inScope = keys.companyInScope ?? this.rememberedCompany() ?? this.presence[0]?.id;
        this.rememberCompany(this.inScope);
        /*
         * Tambem antes do `last`, e pela mesma razao que a lista de empresas: o que a
         * pessoa pode ver depende de qual empresa esta em escopo, entao decidir a tela
         * de abertura antes de saber qual e ela mandava o administrador de empresa para
         * "Meus dados" - `administersInScope` respondia sobre uma empresa indefinida.
         */
        if (last) {
            // O HUB sozinho nao e uma tela: ele abre na primeira que o leitor pode
            // ver. Era o que o `RoleRedirect` do painel antigo fazia na raiz das rotas.
            await this.flipToLanding(intent);
            return false;
        }
        keys.parentSlot = this.contentSlot;
        this.showCompanyChooser();
        this.showIdentity();
        this.showCompanyLabel();
        this.showNavigation();
        this.showThemes();
        this.parentSlot(this.scope);
        return true;
    }
    // ========== O QUE AS TELAS DE DENTRO PEDEM ==========
    get roles() {
        return this.app.currentSession?.roles ?? [];
    }
    /** Se o leitor tem algum dos papeis pedidos. Usado para esconder secao. */
    allows(wanted) {
        return hasAnyRole(this.roles, wanted);
    }
    /**
     * Se ha empresa a ESCOLHER, e quais.
     *
     * A pergunta mora aqui, e nao em cada tela. Ela estava repetida em sete lugares
     * como `allows(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN'])` - o que era a resposta certa
     * enquanto um administrador de empresa tinha exatamente uma. Com a mesma pessoa
     * podendo administrar varias, a resposta deixou de ser so o papel, e sete copias
     * de uma regra que mudou sao sete chances de uma delas ficar para tras.
     *
     * O administrador geral escolhe entre TODAS (e "nenhuma" quer dizer todas); o de
     * empresa escolhe entre as dele, e so quando ha mais de uma.
     */
    get canChooseCompany() {
        return this.allows(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']) || this.companies.length > 1;
    }
    /** As empresas entre as quais escolher. Vazia quando a escolha e "todas". */
    get myCompanies() {
        return this.companies;
    }
    /**
     * A empresa que esta sendo administrada AGORA.
     *
     * E o que as telas de dentro consultam em vez de perguntar o papel: para quem
     * administra uma, e ela; para quem administra varias, e a escolhida no cabecalho.
     * `undefined` para o administrador geral, para quem ausencia quer dizer todas.
     */
    get companyInScope() {
        return this.allows(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']) ? undefined : this.inScope;
    }
    /**
     * A empresa em vigor, mas so quando ela e uma das ATENDIDAS.
     *
     * E o que as telas do PROFISSIONAL mandam ao servidor, no lugar de
     * `companyInScope`. As duas coincidem para o profissional puro - ali `presence` so
     * tem atendidas -, e divergem para quem acumula administrar com atender: o
     * cabecalho dele lista tambem as administradas, e mandar uma delas a um endpoint de
     * profissional e pedir 400 (ver `served`).
     *
     * `undefined` quando a empresa em vigor nao e atendida: o servidor entende a
     * ausencia como "as minhas", e quem nao atende nenhuma recebe lista vazia - que e o
     * que a regra de 17/09/2026 determina - em vez de um erro.
     */
    get companyServedInScope() {
        return this.served.some((company) => company.id === this.inScope) ? this.inScope : undefined;
    }
    /**
     * Se a pessoa ADMINISTRA a empresa em escopo.
     *
     * O administrador GERAL administra todas - para ele nao ha empresa em escopo, e
     * "nenhuma" quer dizer todas. Enquanto a lista nao chegou vale o "sim", pela mesma
     * razao de `servesInScope`: uma falha de rede nao pode fechar o painel de quem
     * administra.
     */
    get administersInScope() {
        if (this.allows(CAN_SEE_COMPANIES)) {
            return true;
        }
        if (!this.companiesKnown) {
            return true;
        }
        return this.companies.some((company) => company.id === this.inScope);
    }
    /**
     * Se a pessoa ATENDE a empresa em escopo.
     *
     * Enquanto a resposta nao chegou, vale o "sim": ver `servedKnown`. Esconder a unica
     * tela do profissional por uma falha de rede seria pior do que mostra-la vazia.
     */
    get servesInScope() {
        return !this.servedKnown || this.served.some((company) => company.id === this.inScope);
    }
    /**
     * A empresa que uma tela de dentro deve PUBLICAR no endereco.
     *
     * Nada, quando quem escolhe e o CABECALHO: ali a empresa ja viaja em `ce`, que e
     * parametro DESTE place, e o mesmo numero publicado por dois donos foi o que fez a
     * troca de empresa abrir a tela na empresa anterior. As telas leem
     * `keys.companyId ?? owner.companyInScope`, entao o parametro da tela - escrito na
     * montagem, com a empresa de antes - vencia o do cabecalho, que acabara de mudar.
     *
     * Para o administrador GERAL, que nao tem cabecalho, nada muda: ali a empresa e
     * filtro daquela tela, com dono unico e "todas" como valor legitimo.
     */
    companyIdInAddress(chosen) {
        return this.hasHeaderChooser ? undefined : chosen;
    }
    /**
     * Se a escolha de empresa esta no CABECALHO.
     *
     * Quando esta, as telas nao desenham a delas: seria o mesmo controle duas vezes na
     * mesma pagina, e duas coisas que fazem a mesma coisa acabam discordando.
     *
     * Para o administrador GERAL continua sendo por tela - ali a empresa e FILTRO
     * daquela lista, com "todas" como valor legitimo, e nao o contexto de trabalho.
     */
    get hasHeaderChooser() {
        return this.presence.length > 1;
    }
    /**
     * A linha abaixo do nome: QUAL empresa, e nao o papel.
     *
     * "Empresa" ali dizia o papel de quem esta logado - informacao que o menu ja da, e
     * que nao muda nunca. O que muda, e o que decide o que cada tela mostra, e em qual
     * empresa se esta.
     */
    showCompanyLabel() {
        if (this.presence.length === 0) {
            return;
        }
        const atual = this.presence.find((company) => company.id === this.inScope) ?? this.presence[0];
        const nome = this.knownName(atual);
        /*
         * A profissao vem DEPOIS do nome da empresa, e nao no lugar dele.
         *
         * Ela e por vinculo, entao dizer "Nutricionista" sozinho nao diz onde - e o
         * profissional que atende dois clientes exerce coisas diferentes em cada um. O
         * separador e um ponto medio, que nao sugere hierarquia entre os dois.
         */
        this.scope.roleLabel = atual.profissao ? `${nome} · ${atual.profissao}` : nome;
    }
    /**
     * As duas listas numa, sem repetir empresa.
     *
     * Quem administra uma e atende outra esta nas duas listas; quem administra E atende
     * a MESMA aparece duas vezes, e ali o vinculo de profissional e que traz a
     * profissao - por isso ele tem precedencia no campo.
     */
    uniteCompanies(atendidas, administradas) {
        /*
         * A ordem do SERVIDOR e preservada - nao se reordena aqui.
         *
         * As duas listas ja vem da mais recente para a mais antiga, e e dessa ordem que
         * sai a empresa de abertura. Uma ordenacao alfabetica aqui - que era o que
         * havia - jogava fora aquela informacao e fazia o painel abrir sempre no "A".
         *
         * As ADMINISTRADAS vem primeiro no caso raro de alguem administrar uma empresa
         * e atender outra: sem a data dos dois vinculos no mesmo DTO nao ha como
         * compara-los, e o papel de maior alcance e o melhor palpite. Se esse caso
         * aparecer de verdade, a saida e trazer a data e ordenar de fato.
         */
        const porId = new Map();
        for (const company of administradas) {
            porId.set(company.id, company);
        }
        for (const company of atendidas) {
            porId.set(company.id, { ...porId.get(company.id), ...company });
        }
        return [...porId.values()];
    }
    /** O nome pelo qual a empresa e conhecida - fantasia quando ha, razao quando nao. */
    knownName(company) {
        return company.nomeFantasia?.trim() ? company.nomeFantasia : company.razaoSocial;
    }
    /**
     * A ULTIMA empresa escolhida, lembrada por conta e por navegador.
     *
     * No `localStorage` e nao no servidor: e uma preferencia de contexto de trabalho,
     * nao um dado do cadastro - e o endereco, que e quem manda, ja viaja no link. A
     * chave leva o id da CONTA porque duas pessoas usam o mesmo navegador: sem isso,
     * quem entrasse depois abriria na empresa de quem saiu.
     *
     * Devolve `undefined` quando a lembrada nao esta mais entre as empresas da pessoa -
     * o vinculo pode ter sido desfeito desde a ultima visita, e abrir numa empresa que
     * ela nao alcanca mais renderia uma recusa do servidor sem explicacao na tela.
     */
    rememberedCompany() {
        const conta = this.app.currentSession?.id;
        if (conta === undefined) {
            return undefined;
        }
        const guardado = safeRead(`${COMPANY_MEMORY}:${conta}`);
        if (guardado === null) {
            return undefined;
        }
        const id = Number(guardado);
        return this.presence.some((company) => company.id === id) ? id : undefined;
    }
    rememberCompany(companyId) {
        const conta = this.app.currentSession?.id;
        if (conta === undefined || companyId === undefined) {
            return;
        }
        safeWrite(`${COMPANY_MEMORY}:${conta}`, String(companyId));
    }
    /**
     * A empresa com que uma tela de dentro ABRE: a do endereco; sem ela, a padrao de
     * quem esta logado; e, para o administrador geral, a ultima que ele escolheu em
     * QUALQUER tela (pedido de 19/10/2026).
     *
     * Era `keys.companyId ?? owner.companyInScope` em cinco telas. O administrador geral
     * nao tem empresa em escopo - para ele a empresa e filtro de cada tela -, entao trocar
     * de modulo o devolvia a "todas" e ele escolhia a mesma empresa de novo em cada um.
     * Quem escolhe no CABECALHO ja era lembrado (`rememberedCompany`) e nao passa pela
     * memoria daqui: `companyInScope` responde antes.
     */
    companyOnArrival(inAddress) {
        return inAddress ?? this.companyInScope ?? this.rememberedFilterCompany();
    }
    /**
     * Guarda a escolha do seletor de uma tela, para as outras abrirem nela.
     *
     * "Todas" tambem e escolha, e fica lembrada como tal (texto vazio): quem limpou o
     * filtro num modulo nao espera reencontra-lo preenchido no seguinte. So vale para
     * quem escolhe POR TELA - o administrador geral; para os demais a empresa e o
     * contexto do cabecalho, que tem a memoria dele.
     */
    rememberFilterCompany(companyId) {
        const conta = this.app.currentSession?.id;
        if (conta === undefined || !this.choosesPerScreen) {
            return;
        }
        safeWrite(`${FILTER_COMPANY_MEMORY}:${conta}`, companyId === undefined ? '' : String(companyId));
    }
    rememberedFilterCompany() {
        const conta = this.app.currentSession?.id;
        if (conta === undefined || !this.choosesPerScreen) {
            return undefined;
        }
        // Uma empresa que deixou de existir volta pelo aviso do seletor
        // (`MISSING_COMPANY`), que a troca por "todas" - e isso tambem fica lembrado.
        return safeId(Number(safeRead(`${FILTER_COMPANY_MEMORY}:${conta}`) || undefined));
    }
    /** Se a empresa e filtro de cada tela, e nao contexto do cabecalho. */
    get choosesPerScreen() {
        return this.allows(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']);
    }
    /** O seletor do cabecalho, ou `undefined` quando nao ha o que escolher. */
    showCompanyChooser() {
        if (this.presence.length <= 1) {
            this.scope.companySelector = undefined;
            return;
        }
        this.scopeSelector.restrictTo(this.presence);
        const atual = this.presence.find((company) => company.id === this.inScope);
        void this.scopeSelector.showSelected(this.inScope, atual && this.knownName(atual));
        this.scope.companySelector = this.scopeSelector.scope;
    }
    /**
     * Trocar a empresa no cabecalho e uma VIAGEM, e nao um recarregamento por dentro.
     *
     * A empresa e o contexto de tudo o que a tela consultou, e quem responde por essa
     * coerencia e o `applyParameters` de cada place - o framework caminha do HUB para
     * baixo e cada um relê o que precisa. Antes disto a casca avisava a tela da frente
     * por um `watchCompany`, e a tela refazia a consulta dela: funcionava enquanto o
     * que mudava eram os DADOS, e nao o que a pessoa E na empresa nova.
     *
     * Porque e isso o que muda junto: **quem administra a empresa A e atende a B nao ve
     * o mesmo menu nas duas.** Sem a viagem, ela trocaria para a empresa em que so
     * atende com o Dashboard ainda aberto - uma tela que o servidor recusa.
     *
     * Por isso o destino e decidido aqui: a mesma secao quando ela continua valendo na
     * empresa nova, e a tela de abertura do perfil quando nao.
     */
    async onChooseCompany(companyId) {
        if (companyId === undefined || companyId === this.inScope) {
            return;
        }
        this.inScope = companyId;
        this.rememberCompany(companyId);
        /*
         * Nada de desenhar aqui: o cabecalho, o rotulo e o menu sao refeitos pelo
         * `applyParameters` deste mesmo presenter, que a viagem abaixo faz rodar. Fazer
         * as duas coisas deixaria a casca desenhada duas vezes por clique.
         */
        if (this.section && this.allowsSection(this.section)) {
            await this.flipToSection(this.section);
            return;
        }
        await this.flipToLanding();
    }
    /**
     * Quem ve cada secao - as mesmas tres perguntas que o painel antigo faz em
     * `utills/dataValue.ts` para montar o menu e em `ProtectRouter` para guardar a
     * rota. Uma fonte so, usada pelos dois, e o que impede menu e guarda de
     * discordarem.
     */
    allowsSection(section) {
        switch (section) {
            // `canSeeCollaborators`: inclui o admin de EMPRESA. O antigo mostra
            // Painel e Adocao e Engajamento ao mesmo grupo que ve Colaboradores.
            //
            // E preciso administrar a empresa EM ESCOPO: quem administra uma e atende
            // outra nao administra as duas, e o menu tem de dizer isso.
            case 'dashboard':
            case 'user-base':
            case 'collaborators':
                return this.allows(CAN_SEE_COLLABORATORS) && this.administersInScope;
            // `canSeeCompanies`: so admin, nao o admin de empresa. Unica secao que
            // NAO depende da empresa em escopo - ela e a lista de todas elas.
            case 'companies':
                return this.allows(CAN_SEE_COMPANIES);
            // O ICP compara as empresas entre si: e do mesmo administrador que ve todas.
            // E o conjunto que o `@PreAuthorize` de `/icp/**` aceita.
            case 'icp':
                return this.allows(CAN_SEE_COMPANIES);
            // Quem administra a empresa administra quem faz parte dela - e o admin
            // geral tambem, escolhendo de qual empresa fala. E o mesmo conjunto que
            // o `@PreAuthorize` de `/company/users` e `/company/links` aceita.
            case 'company-users':
                return this.allows(CAN_SEE_COLLABORATORS) && this.administersInScope;
            /*
             * `canSeeProfessionals`: profissional que NAO acumula admin. A negativa
             * faz parte da regra - um super admin tambem carrega ROLE_PROFESSIONAL.
             *
             * E preciso ATENDER a empresa em escopo, e nao so ter o papel. Desde a
             * regra de 17/09/2026 o profissional so alcanca colaborador de empresa em
             * que esta lotado, e `PatientSpecification` fecha a lista de quem nao esta
             * na empresa: a tela existiria para abrir sempre vazia. Quem acumula
             * administrar com atender ainda via o item aqui e um 400 ao entrar, porque
             * o cabecalho dele aponta para uma empresa ADMINISTRADA - a conta 268 da
             * base de QA administra seis e nao atende nenhuma.
             */
            case 'monitoring':
                return this.allows(['ROLE_PROFESSIONAL']) && !this.allows(CAN_SEE_COMPANIES) && this.servesInScope;
            // O relatorio de sono serve ao nucleo de saude: quem acompanha
            // atletas, seja pelo cadastro da empresa ou pelos monitoramentos. E o
            // mesmo conjunto que o servidor aceita em `/analytics/sleep_report`.
            case 'sleep-report':
                return ((this.allows(CAN_SEE_COLLABORATORS) && this.administersInScope) ||
                    (this.allows(['ROLE_PROFESSIONAL']) && this.servesInScope));
            // Meus dados: todo mundo que entra no HUB tem.
            case 'my-data':
                return true;
        }
    }
    /**
     * A guarda por tela, que no painel antigo era o `ProtectRouter` repetido em
     * cada rota.
     *
     * Chamada no COMECO do `applyParameters` de cada tela, antes de pedir rede: o
     * framework caminha da raiz, entao o HUB ja rodou e a sessao ja esta em maos.
     * Recusar aqui devolve `false` e interrompe a caminhada - a tela nao chega a
     * montar, como o `<Navigate>` do antigo.
     */
    async allowEntry(section, intent) {
        if (this.allowsSection(section)) {
            return true;
        }
        // Com o intent EM TRANSITO, e nao com `Keys.of(app)`: no meio do flip quem chamou
        // ainda nao foi comitado, e o `of` perderia o que o link acabou de trazer (a
        // empresa de um `?rsa=` recusado, por exemplo).
        LOG.warn(`Section "${section}" refused for roles [${this.roles.join(', ')}]`);
        await this.flipToLanding(intent);
        return false;
    }
    /**
     * Onde cada perfil abre - o `RoleRedirect` do painel antigo.
     *
     * A ordem e a dele: quem tem Painel vai para o Painel; o profissional puro vai
     * para os Monitoramentos, que e a tela que o antigo lhe da na raiz; e quem nao
     * tem nem uma nem outra fica em Meus dados.
     *
     * Com o `intent` em maos, REDIRECIONA em vez de montar o destino do zero - a
     * mesma razao do `CollaboratorsPresenter`: `Keys.of(app)` pergunta aos presenters
     * VIVOS, e durante este proprio flip este presenter ainda nao foi comitado, entao
     * o que acabou de chegar no endereco se perde. Era o que acontecia com a empresa:
     * o link `/#/hub?ce=900` abria na empresa lembrada, e nao na do link. Sem intent -
     * quando a chamada vem de `allowEntry`, que recusa uma secao - nao ha de onde
     * copiar, e o destino nasce do zero como antes.
     */
    async flipToLanding(intent) {
        const destino = this.allowsSection('dashboard')
            ? Places.dashboard
            : this.allowsSection('monitoring')
                ? Places.monitoring
                : Places.myData;
        if (intent) {
            await this.app.flipToIntent(intent.redirect(destino));
            return;
        }
        if (destino === Places.dashboard) {
            await DashboardKeys.of(this.app).flip();
            return;
        }
        if (destino === Places.monitoring) {
            await MonitoringKeys.of(this.app).flip();
            return;
        }
        await MyDataKeys.of(this.app).flip();
    }
    /**
     * Viaja para a RAIZ de uma secao.
     *
     * Raiz, e nao onde se estava: uma secao pode estar aberta num detalhe - a ficha de
     * um colaborador -, e esse detalhe e de uma pessoa da empresa ANTERIOR. O place de
     * cima reencaminha para a listagem sozinho.
     */
    async flipToSection(section) {
        await new SECTION_KEYS[section](this.app).flip();
    }
    /**
     * Qual secao esta aberta, dito pela tela de dentro.
     *
     * Uma tela mais profunda diz o que e; a casca so desenha. A alternativa seria
     * uma casca que conhece todos os modulos, que e justamente o que esta
     * arquitetura evita.
     */
    showSection(section, title, subtitle) {
        this.section = section;
        this.scope.sectionTitle = title;
        this.scope.sectionIcon = SECTION_ICONS[section];
        // Escrito sempre, mesmo vazio: a casca sobrevive a troca de tela, e um
        // subtitulo que nao fosse limpo ficaria explicando a tela anterior.
        this.scope.sectionSubtitle = subtitle;
        this.showNavigation();
    }
    /**
     * O menu, montado a partir do que o leitor pode ver.
     *
     * Dois grupos, com os nomes e a ORDEM do painel antigo: em "PRINCIPAL" o
     * Dashboard, a Adocao e Engajamento, os Monitoramentos Compartilhados, as Empresas
     * e os Colaboradores, nessa sequencia; em "SISTEMA" as Configuracoes e o Sair.
     *
     * `ROLE_ADMIN` e `ROLE_SUPER_ADMIN` andam sempre juntos: a API so devolve o
     * primeiro, mas o segundo e o que esta no banco.
     */
    showNavigation() {
        const principal = [];
        if (this.allowsSection('dashboard')) {
            principal.push(this.buildNavItem('Dashboard', 'dashboard', async () => {
                await DashboardKeys.of(this.app).flip();
            }));
        }
        if (this.allowsSection('user-base')) {
            principal.push(this.buildNavItem('Adoção e Engajamento', 'user-base', async () => {
                await UserBaseKeys.of(this.app).flip();
            }));
        }
        if (this.allowsSection('monitoring')) {
            principal.push(this.buildNavItem('Monitoramentos Compartilhados', 'monitoring', async () => {
                await MonitoringKeys.of(this.app).flip();
            }));
        }
        if (this.allowsSection('sleep-report')) {
            principal.push(this.buildNavItem('Relatório de Sono', 'sleep-report', async () => {
                await SleepReportKeys.of(this.app).flip();
            }));
        }
        if (this.allowsSection('companies')) {
            principal.push(this.buildNavItem('Empresas', 'companies', async () => {
                await CompaniesKeys.of(this.app).flip();
            }));
        }
        // Logo depois de Empresas, como no painel antigo: e a mesma lista, vista pelo valor
        // que cada uma tira da plataforma.
        if (this.allowsSection('icp')) {
            principal.push(this.buildNavItem('Cliente Ideal (ICP)', 'icp', async () => {
                await IcpKeys.of(this.app).flip();
            }));
        }
        if (this.allowsSection('collaborators')) {
            principal.push(this.buildNavItem('Colaboradores', 'collaborators', async () => {
                await CollaboratorsKeys.of(this.app).flip();
            }));
        }
        /*
         * "Acesso ao painel", e nao "Usuarios da Empresa" (18/09/2026).
         *
         * "Usuario" era a palavra ambigua: colaborador TAMBEM e usuario, e as duas telas
         * falam de pessoas - uma precisava abrir as duas para lembrar qual era qual. O par
         * agora se separa sozinho pelo nome: em Colaboradores estao os MEDIDOS, que usam o
         * app; aqui estao os que ENTRAM no painel, administrando ou atendendo.
         *
         * Fica depois de Colaboradores pela mesma razao de sempre: e a mesma empresa vista
         * pelo lado de quem a administra.
         */
        if (this.allowsSection('company-users')) {
            principal.push(this.buildNavItem('Acesso ao painel', 'company-users', async () => {
                await CompanyUsersKeys.of(this.app).flip();
            }));
        }
        /*
         * "Configurações" no menu e "Meus Dados" no cabecalho - o descasamento e do
         * painel antigo, reproduzido de proposito. Escolher um dos dois nomes e
         * decisao de produto, e nao de porte.
         */
        const sistema = [
            this.buildNavItem('Configurações', 'my-data', async () => {
                await MyDataKeys.of(this.app).flip();
            }, 'system'),
            // Sair nao e secao: nunca fica marcado, nao tem endereco e nao ha place
            // para o qual leve - o `MainPresenter` derruba a sessao e a porta
            // reaparece sozinha.
            this.buildSignOutItem()
        ];
        this.scope.navigation = [this.buildNavGroup('PRINCIPAL', principal), this.buildNavGroup('SISTEMA', sistema)];
    }
    /**
     * Quem esta na tela, para a casca desenhar.
     *
     * O nome NAO e o `username` da sessao, que e o e-mail: o painel antigo pede
     * `/company/current` ou `/professional/current` e mostra o nome fantasia da
     * empresa ou o nome social da pessoa. Enquanto isso nao chega ele desenha um
     * girador, e nao o e-mail - por isso o `identityLoading`.
     */
    showIdentity() {
        const session = this.app.currentSession;
        this.scope.roleLabel = session ? headerRoleLabel(session.roles) : undefined;
        if (!session) {
            this.scope.userName = undefined;
            this.scope.identityLoading = false;
            this.identityAsked = false;
            return;
        }
        /*
         * Uma vez por sessao, e SEM reescrever o nome ja resolvido.
         *
         * Este metodo roda a cada `applyParameters` do HUB, ou seja a cada
         * navegacao. Escrever `session.username` aqui sem o guarda desfazia o nome
         * que `/company/current` trouxe: o cabecalho mostrava "LVA HEALTH" no painel
         * e voltava para o nome da pessoa ao trocar de tela. E pedir o nome de novo
         * em cada navegacao seria uma requisicao por clique.
         */
        if (this.identityAsked) {
            return;
        }
        this.scope.userName = session.username;
        this.identityAsked = true;
        void this.loadIdentity();
    }
    /**
     * As empresas que esta pessoa administra. Uma vez por sessao.
     *
     * So para quem administra: o administrador geral nao tem lista - para ele a
     * escolha e entre TODAS, e quem a oferece e o seletor que busca no servidor.
     */
    /**
     * As empresas da pessoa, lidas da SESSAO.
     *
     * Quem as busca e o apresentador de aplicacao, quando a sessao nasce (o login, ou a
     * sessao restaurada no F5) - ver `company-access.ts`. Aqui, no caso normal, nao ha
     * rede: o `await` so vai ao servidor se uma metade falhou no login, e entao pergunta
     * de novo so ela. Uma falha de rede no login nao pode valer pela sessao inteira,
     * porque o menu decide por esta resposta.
     */
    async loadCompanies() {
        const access = await this.app.ensureCompanyAccess();
        /*
         * O profissional tambem tem empresa, e o cabecalho dele dizia so "Profissional".
         *
         * Aquilo e o PAPEL - o que o menu ja diz, e o que nao muda nunca -, no lugar
         * onde quem administra le em qual empresa esta. As atendidas entram em
         * `presence` com a profissao de cada vinculo, e nao em `companies`: ele nao
         * administra nada, e as telas de gestao nao devem passar a achar que sim.
         *
         * ## Trocar de empresa aqui TROCA o que ele ve, e desde 17/09/2026
         *
         * O seletor do profissional foi informativo enquanto a pergunta esteve aberta -
         * ele dizia em qual empresa a pessoa esta e o que exerce nela, e as duas telas
         * dele listavam todo paciente vinculado, de qualquer empresa. O cliente
         * respondeu (item 13 de `.claude/docs/validar-com-cliente.md`): ele so alcanca
         * colaborador de empresa em que esta LOTADO.
         *
         * O que aquela pergunta tinha de dificil continua valendo a leitura, porque
         * explica quem ficou de fora: o vinculo tem DUAS origens - a empresa atribuindo
         * (`assignPatient`, que exige que o paciente seja dela) e o PACIENTE
         * compartilhando o historico pelo app, que nao passa por empresa alguma. Na base
         * de QA a segunda e a maioria: a conta de teste tem 13 pacientes, 11 deles sem
         * empresa. Esses 11 sairam da tela do profissional, e quem os ve e so o
         * administrador geral.
         */
        this.served = [...(access.served ?? [])];
        this.servedKnown = access.served !== undefined;
        this.companies = [...(access.administered ?? [])];
        this.companiesKnown = access.administered !== undefined;
        /*
         * A uniao das duas listas INTEIRAS. `presence` mistura atendidas e administradas
         * de proposito - ela responde "onde eu estou", e quem a le e o cabecalho.
         */
        const administra = this.allows(['ROLE_COMPANY_ADMIN']);
        this.presence = administra ? this.uniteCompanies(this.served, this.companies) : this.served;
    }
    async loadIdentity() {
        this.scope.identityLoading = true;
        try {
            if (this.allows(['ROLE_COMPANY_ADMIN'])) {
                /*
                 * O cabecalho diz a PESSOA, e a linha de baixo diz a empresa.
                 *
                 * Ali sempre esteve o nome da EMPRESA - e enquanto a linha de baixo
                 * dizia o papel ("Empresa"), aquilo respondia "quem esta logado" de
                 * forma aceitavel. Desde que a linha de baixo passou a dizer a
                 * empresa, o mesmo nome aparecia duas vezes, uma embaixo da outra; e
                 * para quem administra mais de uma ele ainda contradizia a tela
                 * quando ela estava olhando a outra empresa.
                 */
                this.scope.userName = await this.nameFromAccount('Usuário');
            }
            else if (this.allows(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_PROFESSIONAL'])) {
                const profile = await api.fetchCurrentProfessional();
                this.scope.userName = profile
                    ? accountName(profile.user) || this.scope.userName || 'Usuário'
                    : await this.nameFromAccount('Usuário');
            }
        }
        catch (caught) {
            // Nome e enfeite de cabecalho: falhar aqui nao derruba a tela. O painel
            // antigo tambem so registra e segue com o que tem.
            LOG.warn('Nao foi possivel obter o nome de quem esta logado', caught);
        }
        finally {
            this.scope.identityLoading = false;
        }
    }
    /**
     * O nome pela CONTA, quando o perfil do papel nao existe.
     *
     * `/professional/current` e `/company/current` exigem o papel deles, e um admin
     * que nao seja nenhum dos dois nao tem perfil para pedir - ele so se enxerga em
     * `/user/find_current`, que nao pede papel algum. Sem esta volta o cabecalho
     * desse admin ficava com o `username` da sessao e, pior, o 403 do perfil era
     * lido como sessao perdida e o expulsava para o login.
     */
    async nameFromAccount(fallback) {
        const account = await api.fetchCurrentUser();
        return accountName(account) || this.scope.userName || fallback;
    }
    buildNavGroup(name, items) {
        const scope = new NavGroupScope();
        scope.identity = name;
        scope.name = name;
        scope.items = items;
        scope.update = this.update;
        return scope;
    }
    buildNavItem(label, section, onSelect, group = 'main') {
        const scope = new NavItemScope();
        scope.identity = label;
        scope.label = label;
        scope.icon = SECTION_ICONS[section];
        scope.href = SECTION_HREFS[section];
        scope.current = this.section === section;
        scope.group = group;
        scope.onSelect = this.action(onSelect);
        scope.update = this.update;
        return scope;
    }
    // ========== APARENCIA ==========
    /**
     * Troca a aparencia - o menu do nome e "Meus dados" passam por aqui.
     *
     * Quem guarda e grava e o `MainPresenter`, porque o tema e da SESSAO (vale ate na
     * tela de entrada, que fica sempre em Praia). Aqui so se redesenha a marca de qual
     * esta escolhido.
     */
    async choosePanelTheme(theme) {
        await this.app.choosePanelTheme(theme);
        this.showThemes();
    }
    get panelTheme() {
        return this.app.panelTheme;
    }
    /**
     * As tres opcoes de aparencia, prontas para um scope - o do menu do nome ou o de
     * "Meus dados". `update` e o ciclo de quem as mostra, e `refresh` e como esse quem
     * redesenha a marca depois da troca; o menu do nome se redesenha sozinho.
     */
    themeOptions(update, refresh = NOOP_VOID) {
        const current = this.app.panelTheme;
        return PANEL_THEMES.map((theme) => {
            const scope = new ThemeOptionScope();
            scope.identity = theme;
            scope.value = theme;
            scope.label = PANEL_THEME_LABELS[theme];
            scope.current = theme === current;
            scope.onSelect = this.action(async () => {
                await this.choosePanelTheme(theme);
                refresh();
            });
            scope.update = update;
            return scope;
        });
    }
    showThemes() {
        this.scope.themes = this.themeOptions(this.update);
    }
    buildSignOutItem() {
        const scope = new NavItemScope();
        scope.identity = 'Sair';
        scope.label = 'Sair';
        scope.icon = 'door-open';
        scope.href = '';
        scope.current = false;
        scope.group = 'system';
        scope.onSelect = this.action(async () => {
            await this.app.confirmSignOut();
        });
        scope.update = this.update;
        return scope;
    }
}
//# sourceMappingURL=restricted.presenter.js.map